<div class="home-content">
  <app-page-header
    title="Dashboard"
    [icon]="pageIcon"
    subtitle="File processing activity and items that need attention"
    testId="home-page-header"
  ></app-page-header>

  <div class="home-content__body">
    @if (!adminSettings) {
      <div class="loading-panel">
        <app-lds-ellipsis [isLoading]="true"></app-lds-ellipsis>
      </div>
    } @else if (!adminSettings?.enabled) {
      <section aria-labelledby="processing-summary-heading">
        <div class="home-content__summary-header">
          <h2
            id="processing-summary-heading"
            class="home-content__section-title"
          >
            Processing summary
          </h2>
          <span class="home-content__range-pill">Last 30 days</span>
        </div>

        @if (filesError) {
          <app-state-panel
            icon="error_outline"
            title="Unable to load processing activity"
            message="We couldn't load file processing activity. Try again."
            retryLabel="Try again"
            testId="home-files-error"
            (retry)="getFiles()"
          ></app-state-panel>
        } @else {
          <div class="home-content__summary-grid">
            <app-section-card
              title="Credit limit increases"
              [collapsible]="false"
              icon="trending_up"
              iconTone="primary"
              testId="home-cli-summary-card"
            >
              <hr class="home-content__summary-divider" />
              <div class="home-content__metrics">
                <div class="home-content__metric">
                  @if (filesLoading) {
                    <app-skeleton class="home-content__metric-value-skeleton" />
                  } @else {
                    <span class="home-content__metric-value">{{
                      cliBatchFiles.length
                    }}</span>
                  }
                  <span class="home-content__metric-label"
                    >files processed</span
                  >
                </div>
                <div class="home-content__metric">
                  @if (filesLoading) {
                    <app-skeleton class="home-content__metric-value-skeleton" />
                  } @else {
                    <span
                      class="home-content__metric-value home-content__metric-value--success"
                      >{{ cliSuccesses }}</span
                    >
                  }
                  <span class="home-content__metric-label">successful</span>
                </div>
                <div class="home-content__metric">
                  @if (filesLoading) {
                    <app-skeleton class="home-content__metric-value-skeleton" />
                  } @else {
                    <span
                      class="home-content__metric-value home-content__metric-value--failure"
                      >{{ cliFailures }}</span
                    >
                  }
                  <span class="home-content__metric-label">with failures</span>
                </div>
              </div>
              <div class="home-content__summary-actions">
                <button
                  type="button"
                  color="primary"
                  mat-flat-button
                  class="home-content__submit-button"
                  [routerLink]="['/submit']"
                >
                  <mat-icon aria-hidden="true">add_circle_outline</mat-icon>
                  Submit increase file
                </button>
                <a
                  class="home-content__summary-link"
                  [routerLink]="['/processedfiles']"
                >
                  View submitted files
                  <mat-icon aria-hidden="true">arrow_forward</mat-icon>
                </a>
              </div>
            </app-section-card>

            <app-section-card
              title="Credit limit decreases"
              [collapsible]="false"
              icon="trending_down"
              iconTone="error"
              testId="home-cld-summary-card"
            >
              <hr class="home-content__summary-divider" />
              <div class="home-content__metrics">
                <div class="home-content__metric">
                  @if (filesLoading) {
                    <app-skeleton class="home-content__metric-value-skeleton" />
                  } @else {
                    <span class="home-content__metric-value">{{
                      cldBatchFiles.length
                    }}</span>
                  }
                  <span class="home-content__metric-label"
                    >files processed</span
                  >
                </div>
                <div class="home-content__metric">
                  @if (filesLoading) {
                    <app-skeleton class="home-content__metric-value-skeleton" />
                  } @else {
                    <span
                      class="home-content__metric-value home-content__metric-value--success"
                      >{{ cldSuccesses }}</span
                    >
                  }
                  <span class="home-content__metric-label">successful</span>
                </div>
                <div class="home-content__metric">
                  @if (filesLoading) {
                    <app-skeleton class="home-content__metric-value-skeleton" />
                  } @else {
                    <span
                      class="home-content__metric-value home-content__metric-value--failure"
                      >{{ cldFailures }}</span
                    >
                  }
                  <span class="home-content__metric-label">with failures</span>
                </div>
              </div>
              <div class="home-content__summary-actions">
                <button
                  type="button"
                  color="primary"
                  mat-flat-button
                  class="home-content__submit-button"
                  [routerLink]="['/submit']"
                >
                  <mat-icon aria-hidden="true" fontSet="material-icons-outlined"
                    >do_not_disturb_on</mat-icon
                  >
                  Submit decrease file
                </button>
                <a
                  class="home-content__summary-link"
                  [routerLink]="['/processedfiles']"
                >
                  View submitted files
                  <mat-icon aria-hidden="true">arrow_forward</mat-icon>
                </a>
              </div>
            </app-section-card>
          </div>
        }
      </section>

      @if (!filesError) {
        <section aria-labelledby="attention-heading">
          <div class="home-content__attention-header">
            <h2 id="attention-heading" class="home-content__section-title">
              @if (filesLoading) {
                Last files with failures
              } @else {
                Last {{ attentionRows.length }} files with failures
              }
            </h2>
            <a
              class="home-content__summary-link"
              [routerLink]="['/processedfiles']"
            >
              View all files
              <mat-icon aria-hidden="true">arrow_forward</mat-icon>
            </a>
          </div>

          @if (filesLoading || attentionRows.length) {
            <div data-testid="home-attention-table">
              <app-data-table
                [columns]="attentionColumns"
                [rows]="attentionRows"
                [loading]="filesLoading"
                [pageSize]="5"
                [compact]="true"
                [showFooter]="false"
                rowHeaderKey="fileName"
                ariaLabel="Files needing attention"
                testId="home-attention-table-grid"
                rowTestId="home-attention-row"
                [trackByFn]="trackByAttentionRow"
              >
                <ng-template appDataTableCell="submittedOn" let-row>
                  {{ row.submittedOn | date: 'MMM d, y' }}
                </ng-template>
                <ng-template appDataTableCell="failedRecords" let-row>
                  <span class="home-content__failed-cell">
                    <span
                      class="home-content__failed-dot"
                      aria-hidden="true"
                    ></span>
                    {{ row.failedRecords }}
                  </span>
                </ng-template>
              </app-data-table>
              @if (!filesLoading) {
                <p class="home-content__attention-footnote">
                  Showing files with failed records from the selected period.
                </p>
              }
            </div>
          } @else {
            <app-state-panel
              icon="check_circle"
              title="No files need attention"
              message="All files processed within the last 30 days completed without failed records."
              testId="home-attention-empty"
            ></app-state-panel>
          }
        </section>
      }
    } @else {
      <app-state-panel
        icon="engineering"
        title="System under maintenance"
        message="The system is currently under maintenance. Please check back later."
        testId="home-maintenance"
      ></app-state-panel>
    }
  </div>
</div>
